/**
 * MeasurementToolsPanel - UI panel for measurement tools.
 *
 * Provides buttons to activate distance, area, and coordinate measurement tools,
 * unit selectors, and displays measurement results in real-time.
 *
 * Validates: Requirements 19.1, 19.5, 20.1, 20.5, 21.1, 21.2, 21.3, 21.5
 */
import React, { useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Divider,
  type SelectChangeEvent,
} from '@mui/material';
import StraightenIcon from '@mui/icons-material/Straighten';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  setMeasurementMode,
  setDistanceUnits,
  setAreaUnits,
  setCoordinateFormat,
  clearMeasurement,
  type MeasurementMode,
} from '../store/slices/measurementSlice';
import type { DistanceUnits, AreaUnits, CoordinateFormat } from '../services/MeasurementService';
import { formatDistance, formatArea, formatCoordinates } from '../services/MeasurementService';

export const MeasurementToolsPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    activeMode,
    distanceUnits,
    areaUnits,
    coordinateFormat,
    cursorCoordinates,
    clickedCoordinate,
    currentDistance,
    currentArea,
    currentPerimeter,
    isMeasuring,
    distancePoints,
    areaPoints,
  } = useAppSelector((state) => state.measurement);

  const handleModeChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, newMode: MeasurementMode) => {
      dispatch(setMeasurementMode(newMode));
    },
    [dispatch]
  );

  const handleDistanceUnitsChange = useCallback(
    (event: SelectChangeEvent) => {
      dispatch(setDistanceUnits(event.target.value as DistanceUnits));
    },
    [dispatch]
  );

  const handleAreaUnitsChange = useCallback(
    (event: SelectChangeEvent) => {
      dispatch(setAreaUnits(event.target.value as AreaUnits));
    },
    [dispatch]
  );

  const handleCoordinateFormatChange = useCallback(
    (event: SelectChangeEvent) => {
      dispatch(setCoordinateFormat(event.target.value as CoordinateFormat));
    },
    [dispatch]
  );

  const handleClear = useCallback(() => {
    dispatch(clearMeasurement());
  }, [dispatch]);

  const handleCopyCoordinates = useCallback(async () => {
    const coord = clickedCoordinate || cursorCoordinates;
    if (!coord) return;

    const formatted = formatCoordinates(coord, coordinateFormat);
    try {
      await navigator.clipboard.writeText(formatted);
    } catch {
      // Fallback for environments without clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = formatted;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  }, [clickedCoordinate, cursorCoordinates, coordinateFormat]);

  return (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        width: 280,
        maxHeight: '80vh',
        overflow: 'auto',
      }}
      data-testid="measurement-tools-panel"
    >
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        Measurement Tools
      </Typography>

      {/* Tool Selection */}
      <ToggleButtonGroup
        value={activeMode}
        exclusive
        onChange={handleModeChange}
        size="small"
        fullWidth
        aria-label="measurement tool selection"
      >
        <ToggleButton value="distance" aria-label="distance measurement">
          <Tooltip title="Measure Distance">
            <StraightenIcon />
          </Tooltip>
        </ToggleButton>
        <ToggleButton value="area" aria-label="area measurement">
          <Tooltip title="Measure Area">
            <SquareFootIcon />
          </Tooltip>
        </ToggleButton>
        <ToggleButton value="coordinate" aria-label="coordinate display">
          <Tooltip title="Coordinate Display">
            <LocationOnIcon />
          </Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>

      <Divider sx={{ my: 1.5 }} />

      {/* Distance Measurement Panel */}
      {activeMode === 'distance' && (
        <Box data-testid="distance-panel">
          <FormControl size="small" fullWidth sx={{ mb: 1 }}>
            <InputLabel id="distance-units-label">Units</InputLabel>
            <Select
              labelId="distance-units-label"
              value={distanceUnits}
              label="Units"
              onChange={handleDistanceUnitsChange}
              data-testid="distance-units-select"
            >
              <MenuItem value="meters">Meters</MenuItem>
              <MenuItem value="kilometers">Kilometers</MenuItem>
              <MenuItem value="feet">Feet</MenuItem>
              <MenuItem value="miles">Miles</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {isMeasuring
                ? 'Click points on map. Double-click to finish.'
                : distancePoints.length > 0
                  ? 'Measurement complete.'
                  : 'Click on map to start measuring.'}
            </Typography>

            {(currentDistance > 0 || distancePoints.length >= 2) && (
              <Typography variant="h6" color="primary" sx={{ mt: 1 }} data-testid="distance-result">
                {formatDistance(currentDistance, distanceUnits)}
              </Typography>
            )}

            {distancePoints.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                {distancePoints.length} point{distancePoints.length !== 1 ? 's' : ''}
              </Typography>
            )}
          </Box>

          {distancePoints.length > 0 && (
            <Tooltip title="Clear measurement">
              <IconButton size="small" onClick={handleClear} sx={{ mt: 1 }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      {/* Area Measurement Panel */}
      {activeMode === 'area' && (
        <Box data-testid="area-panel">
          <FormControl size="small" fullWidth sx={{ mb: 1 }}>
            <InputLabel id="area-units-label">Area Units</InputLabel>
            <Select
              labelId="area-units-label"
              value={areaUnits}
              label="Area Units"
              onChange={handleAreaUnitsChange}
              data-testid="area-units-select"
            >
              <MenuItem value="square-meters">Square Meters</MenuItem>
              <MenuItem value="square-kilometers">Square Kilometers</MenuItem>
              <MenuItem value="hectares">Hectares</MenuItem>
              <MenuItem value="square-feet">Square Feet</MenuItem>
              <MenuItem value="square-miles">Square Miles</MenuItem>
              <MenuItem value="acres">Acres</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {isMeasuring
                ? 'Click points to define polygon. Double-click to finish.'
                : areaPoints.length > 0
                  ? 'Measurement complete.'
                  : 'Click on map to start measuring area.'}
            </Typography>

            {(currentArea > 0 || areaPoints.length >= 3) && (
              <>
                <Typography variant="h6" color="primary" sx={{ mt: 1 }} data-testid="area-result">
                  {formatArea(currentArea, areaUnits)}
                </Typography>
                <Typography variant="body2" color="text.secondary" data-testid="perimeter-result">
                  Perimeter: {formatDistance(currentPerimeter, distanceUnits)}
                </Typography>
              </>
            )}

            {areaPoints.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                {areaPoints.length} vertices
              </Typography>
            )}
          </Box>

          {areaPoints.length > 0 && (
            <Tooltip title="Clear measurement">
              <IconButton size="small" onClick={handleClear} sx={{ mt: 1 }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      {/* Coordinate Display Panel */}
      {activeMode === 'coordinate' && (
        <Box data-testid="coordinate-panel">
          <FormControl size="small" fullWidth sx={{ mb: 1 }}>
            <InputLabel id="coord-format-label">Format</InputLabel>
            <Select
              labelId="coord-format-label"
              value={coordinateFormat}
              label="Format"
              onChange={handleCoordinateFormatChange}
              data-testid="coordinate-format-select"
            >
              <MenuItem value="decimal-degrees">Decimal Degrees</MenuItem>
              <MenuItem value="degrees-minutes-seconds">DMS</MenuItem>
              <MenuItem value="projected">Projected (Web Mercator)</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Cursor Position:
            </Typography>
            <Typography variant="body2" fontFamily="monospace" data-testid="cursor-coordinates">
              {cursorCoordinates
                ? formatCoordinates(cursorCoordinates, coordinateFormat)
                : 'Move cursor over map'}
            </Typography>
          </Box>

          {clickedCoordinate && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Clicked Position:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography
                  variant="body2"
                  fontFamily="monospace"
                  data-testid="clicked-coordinates"
                >
                  {formatCoordinates(clickedCoordinate, coordinateFormat)}
                </Typography>
                <Tooltip title="Copy to clipboard">
                  <IconButton
                    size="small"
                    onClick={handleCopyCoordinates}
                    data-testid="copy-coordinates-btn"
                    aria-label="copy coordinates to clipboard"
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}

          {!clickedCoordinate && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Click on map to capture coordinates.
            </Typography>
          )}
        </Box>
      )}

      {/* No tool selected */}
      {!activeMode && (
        <Typography variant="body2" color="text.secondary">
          Select a measurement tool above to begin.
        </Typography>
      )}
    </Paper>
  );
};

export default MeasurementToolsPanel;
